import {
  setAllBrandsData,
  setBrandsMetadata,
  setBrandBackendRequirementsData,
  setBrandPropertiesData,
  setTestAgainPopupData,
  setTestAgainPopupMetadata,
} from "../../../features/brandsSlice";
import { setDisplayErrorSnackBar } from "../../../features/userSlice";
import { brandsService } from "../../../services/api/brands.endpoints";
import store from "../../../store";
// Note: these are still brandConfigurationReportSlice actions (Brands has no
// equivalent slice fields for overview/run-process data yet — forking that
// is a separate task from this endpoint/service-layer port). What changes
// here is only *which file makes the HTTP call* — BrandsEndPointLogic /
// brandsService instead of BrandConfigReportEndPointLogic /
// brandConfigReportService — not what state ends up populated.
import {
  setBrandAccountOverviewInfo,
  setBrandIndustryData,
  setBrandOverviewAdvisorSettingsData,
  setBrandOverviewFetcherSettingsData,
  setBrandOverviewLastRunData,
  setBrandOverviewUnifierSettingsData,
  setFetcherConfigData,
  setIsSuccess,
  setObjectSectionData,
  setObjectSectionMetaData,
  setOverviewMetaData,
  setOverviewPerformanceData,
  setProcessFailure,
  setRawTableData,
  setResWorkflowName,
} from "../../../features/brandConfigurationReportSlice";

async function getAllBrandsData(
  oauthTokenHeader: string,
  queryId: string | any,
  website_shared_uids: string[],
  type: string | any,
) {
  try {
    const response = await brandsService.getAllBrandsData(
      oauthTokenHeader,
      queryId,
      website_shared_uids,
      type
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brands data.");
    } else if (response.status === 200) {
      const brandsData = response?.data?.data?.query_output ?? [];
      const brandsMetadata = response?.data?.data?.output_metadata ?? {};

      store.dispatch(setAllBrandsData(brandsData)); // Set the fetched data
      store.dispatch(setBrandsMetadata(brandsMetadata)); // set the metadata
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

// Backend caps a single try_to_connect request at 100 brand ids.
const TRY_TO_CONNECT_MAX_BATCH = 100;

interface TryToConnectFailure {
  brand_id: unknown;
  error?: unknown;
}

// Pulls the failed entries out of a per-brand result list, e.g.
// [{"brand_id":2513,"success":false,"error":"Internal error"}]. Accepts the
// parsed body (success path) or the raw response text (fetchWrapper's error
// path); any other shape yields [].
function getTryToConnectFailures(body: unknown): TryToConnectFailure[] {
  let parsed = body;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter(
    (result) =>
      result && typeof result === "object" && result.success === false,
  );
}

const isInternalError = (error: unknown) =>
  typeof error === "string" && /internal error/i.test(error);

const describeError = (error: unknown) =>
  typeof error === "string" ? error.trim().replace(/\.+$/, "") : "";

// One brand:  "Unable to connect Brand ID 2513 due to an internal error.
//              Please try again later."
// Several:    "Unable to connect 2 brands. Brand ID 2513: internal error.
//              Brand ID 2509: Brand not found. Please try again later."
// "Please try again later." is only added when an internal error is among
// the failures — retrying won't help e.g. a brand that doesn't exist.
function formatTryToConnectFailures(failures: TryToConnectFailure[]) {
  const retryHint = failures.some((f) => isInternalError(f.error))
    ? " Please try again later."
    : "";

  if (failures.length === 1) {
    const [failure] = failures;
    const detail = describeError(failure.error);
    const reason = isInternalError(failure.error)
      ? " due to an internal error"
      : detail
        ? `: ${detail}`
        : "";
    return `Unable to connect Brand ID ${failure.brand_id}${reason}.${retryHint}`;
  }

  const details = failures
    .map((f) => {
      const detail = isInternalError(f.error)
        ? "internal error"
        : describeError(f.error) || "unknown error";
      return `Brand ID ${f.brand_id}: ${detail}.`;
    })
    .join(" ");
  return `Unable to connect ${failures.length} brands. ${details}${retryHint}`;
}

// "Try to Connect" (per-row quick action on Not Connected rows). Accepts one or
// more brand ids. Response contract:
//   200 -> every brand connected; returned as-is.
//   207 -> mixed success/failure; warning snackbar, still returned so the
//          caller refreshes the list for the brands that did connect.
//   400 -> brand not found / invalid body; rejected by fetchWrapper, error
//          snackbar shows the backend's reason.
// Whenever the body is a per-brand result list, the snackbar names each failed
// brand id and its error (formatTryToConnectFailures). If every brand in the
// list failed, it's treated as an error whatever the status code.
// Ids are de-duplicated (the backend would otherwise process a repeated id
// twice) and anything the backend would 400 on — empty list, non-integer or
// non-positive ids, more than 100 ids — is rejected before sending.
async function tryToConnect(oauthTokenHeader: string, brandIds: number[]) {
  try {
    const ids = Array.from(new Set((brandIds ?? []).map(Number)));
    if (ids.length === 0 || ids.some((id) => !Number.isInteger(id) || id <= 0)) {
      throw new Error("Invalid brand selection.");
    }
    if (ids.length > TRY_TO_CONNECT_MAX_BATCH) {
      throw new Error(
        `You can connect up to ${TRY_TO_CONNECT_MAX_BATCH} brands at a time.`,
      );
    }

    const response = await brandsService.tryToConnect(oauthTokenHeader, ids);
    if (!response || (response.status !== 200 && response.status !== 207)) {
      throw new Error("Failed to connect brand.");
    }

    const failures = getTryToConnectFailures(response.data);
    if (failures.length > 0) {
      // Nothing connected — surface as an error, and don't hand back a
      // response the caller would treat as success.
      if (failures.length === response.data.length) {
        throw new Error(formatTryToConnectFailures(failures));
      }
      store.dispatch(
        setDisplayErrorSnackBar({
          message: formatTryToConnectFailures(failures),
          type: "warning",
        }),
      );
    } else if (response.status === 207) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Some brands could not be connected.",
          type: "warning",
        }),
      );
    }
    return response;
  } catch (e: any) {
    const failures = getTryToConnectFailures(e?.text);
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          failures.length > 0
            ? formatTryToConnectFailures(failures)
            : e?.parsedText || e?.message || "Failed to connect brand.",
        type: "error",
      }),
    );
    throw e;
  }
}

// --- Below: ported verbatim (same request structure / same dispatches) from
// BrandConfigReportEndPointLogic.ts, so files under the Brands module call
// through this file instead of the legacy one. BrandConfigReportEndPointLogic
// itself is untouched. ---

async function updateBrandProperties(
  oauthTokenHeader: string,
  data: any,
  brand_shared_uid: any,
) {
  try {
    const response = await brandsService.editBrandProperties(
      oauthTokenHeader,
      data,
      brand_shared_uid,
    );

    if (!response) {
      throw new Error("Failed to update brand properties.");
    }

    if (response.status === 200) {
      return response;
    }

    if (response.status === 207) {
      const errorMessage =
        "Failed to update brand properties. Please try again.";
      throw new Error(errorMessage);
    }

    if (response.status === 400) {
      throw new Error(
        response?.data?.reason ||
          "Something went wrong; please try again later...",
      );
    }

    if (response.status === 502) {
      throw new Error(
        "Server is temporarily unavailable. Please try again later.",
      );
    }

    throw new Error(
      response?.data?.message || "Failed to update brand properties.",
    );
  } catch (e: any) {
    throw e;
  }
}

async function postBrandConfigurationProcess(
  oauthTokenHeader: string,
  data: any,
) {
  try {
    const response = await brandsService.postBrandConfigurationReport(
      oauthTokenHeader,
      data,
    );
    if (!response || !response.status) {
      throw new Error("Failed to process brand integration.");
    } else if (response.status === 200) {
      return response;
    } else if (response.status === 400) {
      throw new Error("Bad request - invalid brand configuration data.");
    } else if (response.status === 502) {
      throw new Error("Bad gateway - service temporarily unavailable.");
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText || e?.message || "Failed to process brand integration.",
        type: "error",
      }),
    );
  }
}

async function getBrandOverviewLastRuns(
  oauthTokenHeader: string | any,
  brand_id: string | any,
) {
  try {
    const response = await brandsService.getBrandOverviewLastRuns(
      oauthTokenHeader,
      brand_id,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand last run information.");
    } else if (response.status === 200) {
      const brandOverviewLastRun = response?.data?.data ?? [];
      store.dispatch(setBrandOverviewLastRunData(brandOverviewLastRun));
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText || "Failed to access brand last run information.",
        type: "error",
      }),
    );
  }
}

async function getBrandOverviewAdvisorSettings(
  oauthTokenHeader: string,
  brand_id: string | any,
  report: string | any,
) {
  try {
    const response = await brandsService.getBrandOverviewAdvisorSettings(
      oauthTokenHeader,
      brand_id,
      report,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand advisor settings.");
    } else if (response.status === 200) {
      const brandOverviewAdvisorSettings = response?.data?.data ?? [];
      store.dispatch(
        setBrandOverviewAdvisorSettingsData({
          reportType: report,
          data: brandOverviewAdvisorSettings,
        }),
      );
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access brand advisor settings.",
        type: "error",
      }),
    );
  }
}

async function getBrandOverviewUnifierSettings(
  oauthTokenHeader: string,
  brand_id: string | any,
  report: string | any,
) {
  try {
    const response = await brandsService.getBrandOverviewUnifierSettings(
      oauthTokenHeader,
      brand_id,
      report,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand unifier settings.");
    } else if (response.status === 200) {
      const brandOverviewUnifierSettings = response?.data?.data ?? [];
      store.dispatch(
        setBrandOverviewUnifierSettingsData({
          reportType: report,
          data: brandOverviewUnifierSettings,
        }),
      );
      return brandOverviewUnifierSettings;
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access brand unifier settings.",
        type: "error",
      }),
    );
  }
}

async function getBrandOverviewFetcherSettings(
  oauthTokenHeader: string,
  reqBody: any,
  report: string | any,
) {
  try {
    const response = await brandsService.getBrandOverviewFetcherSettings(
      oauthTokenHeader,
      reqBody,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand fetcher settings.");
    } else if (response.status === 200) {
      const brandOverviewFetcherSettings = response?.data?.data ?? [];
      store.dispatch(
        setBrandOverviewFetcherSettingsData({
          reportType: report,
          data: brandOverviewFetcherSettings,
        }),
      );
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access brand fetcher settings.",
        type: "error",
      }),
    );
  }
}

async function brandsRunProcess(oauthTokenHeader: string, data: any) {
  try {
    const response = await brandsService.brandsRunProcess(
      oauthTokenHeader,
      data,
    );
    if (!response.status || response.status !== 200) {
      store.dispatch(setIsSuccess(false));
      store.dispatch(
        setProcessFailure({
          isFailed: true,
          message: "Something went wrong while running brand process",
        }),
      );
    } else {
      store.dispatch(setProcessFailure({ isFailed: false, message: "" }));
      store.dispatch(setIsSuccess(true));
      const workflowName = response?.data?.data?.workflow_name;
      if (workflowName) {
        store.dispatch(setResWorkflowName(workflowName));
      } else {
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Workflow name not found in the response",
            type: "error",
          }),
        );
      }
    }
  } catch (e: any) {
    store.dispatch(
      setProcessFailure({
        isFailed: true,
        message: e?.parsedText || "Unknown error occurred",
      }),
    );
  }
}

async function getOverviewPerformance(
  oauthTokenHeader: string | any,
  report: string | any,
  queryId: string | any,
  website_shared_uid: string | any,
  type: string | any,
  brand_id: string | any,
  start_date: string | any,
  end_date: string | any,
  limit: string | any,
) {
  try {
    const response = await brandsService.getOverviewPerformance(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      type,
      brand_id,
      start_date,
      end_date,
      limit,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access overview performance.");
    } else if (response.status === 200) {
      const overViewData = response?.data?.data?.query_output ?? [];
      const overviewMetadata = response?.data?.data?.output_metadata ?? {};

      store.dispatch(
        setOverviewPerformanceData({
          reportType: report,
          data: overViewData,
        }),
      );

      store.dispatch(
        setOverviewMetaData({ reportType: report, data: overviewMetadata }),
      );

      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

async function getObjectsData(
  oauthTokenHeader: string,
  queryId: string | any,
  website_shared_uid: string | any,
  type: string | any,
  brand_id: string | any,
) {
  try {
    const response = await brandsService.getObjectsData(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      type,
      brand_id,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access objects data.");
    } else if (response.status === 200) {
      const objectsData = response?.data?.data?.query_output ?? [];
      const objectsMetadata = response?.data?.data?.output_metadata ?? {};

      store.dispatch(setObjectSectionData(objectsData));
      store.dispatch(setObjectSectionMetaData(objectsMetadata));
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

async function getBrandAccountOverviewInfo(
  oauthTokenHeader: string,
  queryId: string | any,
  website_shared_uid: string | any,
  type: string | any,
  brand_id: string | any,
) {
  try {
    const response = await brandsService.getBrandAccountOverviewInfo(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      type,
      brand_id,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand account overview data.");
    } else if (response.status === 200) {
      const brandAccountOverviewInfo = response?.data?.data?.query_output ?? [];
      store.dispatch(setBrandAccountOverviewInfo(brandAccountOverviewInfo));
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText || "Failed to access brand account overview data.",
        type: "error",
      }),
    );
    return status;
  }
}

async function getAllConfigForFetcher(
  oauthTokenHeader: string,
  biz_layer_query_id: any,
  website_shared_uid: any,
) {
  try {
    const response = await brandsService.getAllConfigForFetcher(
      oauthTokenHeader,
      biz_layer_query_id,
      website_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access fetcher config.");
    } else if (response.status === 200) {
      const fetcherConfig = response?.data?.data ?? [];
      store.dispatch(setFetcherConfigData(fetcherConfig));
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access fetcher config.",
        type: "error",
      }),
    );
  }
}

async function getBrandsForIndustry(
  oauthTokenHeader: string,
  biz_layer_query_id: any,
  website_shared_uid: any,
) {
  try {
    // Matches BrandConfigReportEndPointLogic.getBrandsForIndustry exactly —
    // it also calls the "getAllConfigForFetcher" service function, not a
    // distinct one. Preserved as-is rather than "fixed", per instructions.
    const response = await brandsService.getAllConfigForFetcher(
      oauthTokenHeader,
      biz_layer_query_id,
      website_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access Industry data.");
    } else if (response.status === 200) {
      const data = response?.data?.data ?? [];
      store.dispatch(setBrandIndustryData(data));
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access Industry data.",
        type: "error",
      }),
    );
  }
}

async function getRawTableData(
  oauthTokenHeader: string,
  table_id: string | any,
  start_date: string | any,
  end_date: string | any,
  type: string | any,
  fieldMappingId?: string | any,
) {
  try {
    const response = await brandsService.getRawTableData(
      oauthTokenHeader,
      table_id,
      start_date,
      end_date,
      type,
      fieldMappingId,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access raw table data.");
    } else if (response.status === 200) {
      const rawTableData = response?.data?.data ?? [];
      store.dispatch(setRawTableData(rawTableData));
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

// --- Below: three more functions ported verbatim from
// BrandConfigReportEndPointLogic.ts (getBrandBackendRequirements,
// getBrandProperties, getTestAgainPopupData, manuallyApprove), dispatching
// into brandsSlice's own fields instead of brandConfigurationReportSlice's —
// these power the Brands-owned useBrandProperties.ts hook and
// ValidationStatusDialog.tsx (Edit Properties / Validation Station), which
// no longer read the legacy slice. ---

async function getBrandBackendRequirements(
  oauthTokenHeader: string,
  backend_shared_uid: string,
) {
  try {
    const response = await brandsService.getBrandBackendRequirements(
      oauthTokenHeader,
      backend_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error(
        "Failed to access brand configuration backend requirements.",
      );
    } else if (response.status === 200) {
      const backendRequirements = response?.data?.data ?? [];
      store.dispatch(setBrandBackendRequirementsData(backendRequirements));
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText ||
          "Failed to access brand configuration backend requirements.",
        type: "error",
      }),
    );
  }
}

async function getBrandProperties(
  oauthTokenHeader: string,
  brand_shared_uid: string,
) {
  try {
    const response = await brandsService.getBrandProperties(
      oauthTokenHeader,
      brand_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access brand properties.");
    } else if (response.status === 200) {
      const brandProperties = response?.data?.data ?? [];
      store.dispatch(setBrandPropertiesData(brandProperties));
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access brand properties.",
        type: "error",
      }),
    );
  }
}

async function getTestAgainPopupData(
  oauthTokenHeader: string,
  queryId: string | any,
  website_shared_uid: string | any,
  type: string | any,
  brand_id: string | any,
) {
  try {
    const response = await brandsService.getTestAgainPopupData(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      type,
      brand_id,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access data.");
    } else if (response.status === 200) {
      const popupData = response?.data?.data?.query_output ?? [];
      const popupMetadata = response?.data?.data?.output_metadata ?? {};
      store.dispatch(setTestAgainPopupData(popupData));
      store.dispatch(setTestAgainPopupMetadata(popupMetadata));
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Failed to access data.",
        type: "error",
      }),
    );
    return status;
  }
}

async function manuallyApprove(
  oauthTokenHeader: string,
  data: any,
  brand_id: string | any,
) {
  try {
    const response = await brandsService.manuallyApprove(
      oauthTokenHeader,
      data,
      brand_id,
    );
    if (!response || !response.status) {
      throw new Error("Failed to approve the validation.");
    } else if (response.status === 200) {
      return response;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    return status;
  }
}

export const BrandsEndPointLogic = {
  getAllBrandsData,
  tryToConnect,
  updateBrandProperties,
  postBrandConfigurationProcess,
  getBrandOverviewLastRuns,
  getBrandOverviewAdvisorSettings,
  getBrandOverviewUnifierSettings,
  getBrandOverviewFetcherSettings,
  brandsRunProcess,
  getOverviewPerformance,
  getObjectsData,
  getBrandAccountOverviewInfo,
  getAllConfigForFetcher,
  getBrandsForIndustry,
  getRawTableData,
  getBrandBackendRequirements,
  getBrandProperties,
  getTestAgainPopupData,
  manuallyApprove,
};
